import {
  brandExpressionId,
  Expression as RExpression,
  isStringTemplate,
} from '@frontapp/workflows/expressions';
import {isEmpty, isString, isUndefined} from 'lodash';
import React, {ReactNode} from 'react';
import uuid from 'uuid-random';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  DecisionTreeNodeDataParseExpressionOptionFrontChat,
  ParseExpressionOperationsEnum,
  ParseExpressionResultTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/decisionTreeNodeDataModel';
import {getPropertyLabel} from '../../../../settings/editors/expressions/expressionLabels';
import {Expression, PreExpression} from '../../../../settings/editors/expressions/expressionModel';
import {ExpressionsContext} from '../../../../settings/editors/expressions/expressionsContext';
import {
  ExpressionState,
  ExpressionStateActions,
} from '../../../../settings/editors/expressions/expressionState';
import {
  areInferredTypesEqual,
  BooleanResult,
  DateTimeResult,
  isStringLikeInferredType,
  NumberResult,
} from '../../../../settings/editors/expressions/infer/internal';

/*
 * Types.
 */

export interface ChatBotExpressionsEditor {
  state: ExpressionState;
  actions: ExpressionStateActions;
  context: ExpressionsContext;
  openCreateExpression: () => void;
  isExpressionListPanelVisible: boolean;
  openExpressionList: () => void;
  closeExpressionList: () => void;
}

/*
 * Constants.
 */

const unaryOperations = [
  ParseExpressionOperationsEnum.IS_TRUE,
  ParseExpressionOperationsEnum.IS_FALSE,
  ParseExpressionOperationsEnum.EXISTS,
  ParseExpressionOperationsEnum.NOT_EXIST,
  ParseExpressionOperationsEnum.ELSE,
];

export const defaultChatBotExpressions = Object.freeze(
  castAsPreExpressions(buildDefaultChatBotBuilderExpressions()),
);

/*
 * Helpers.
 */

function buildDefaultChatBotBuilderExpressions(): ReadonlyArray<RExpression> {
  return [
    {
      id: brandExpressionId('local:1'),
      name: getPropertyLabel('page_url'),
      atoms: [
        {
          transform: 'get',
          params: {property: 'page_url'},
        },
      ],
    },
  ];
}

/**
 * For a given result type (the value that will come out of an expression), compute the default options that are
 * available in the parse expression node.
 */
export function buildDefaultParseExpressionOptions(
  resultType: ParseExpressionResultTypesEnum,
): ReadonlyArray<DecisionTreeNodeDataParseExpressionOptionFrontChat> {
  return [
    {
      operation: computeDefaultParseExpressionOperation(resultType),
      value: computeDefaultParseExpressionOptionValue(resultType),
      sourceHandle: uuid(),
    },
    {
      operation: ParseExpressionOperationsEnum.ELSE,
      value: undefined,
      sourceHandle: uuid(),
    },
  ];
}

/** For the given expression result type, determine the operation provided in the default parse expression option. */
function computeDefaultParseExpressionOperation(resultType: ParseExpressionResultTypesEnum) {
  const availableOperations = computeAvailableParseExpressionOperations(resultType);

  if (resultType === ParseExpressionResultTypesEnum.BOOLEAN) {
    return ParseExpressionOperationsEnum.IS_TRUE;
  }

  return availableOperations[0];
}

/** For the given expression result type, determine the default value in the parse expression option.  */
function computeDefaultParseExpressionOptionValue(resultType: ParseExpressionResultTypesEnum) {
  switch (resultType) {
    case ParseExpressionResultTypesEnum.DATE_TIME:
    case ParseExpressionResultTypesEnum.STRING:
      return '';
    case ParseExpressionResultTypesEnum.NUMBER:
      return 0;
    case ParseExpressionResultTypesEnum.BOOLEAN:
    default:
      return undefined;
  }
}

export function computeParseExpressionResultType(expression: Expression): ParseExpressionResultTypesEnum {
  const {inferredType} = expression;

  if (areInferredTypesEqual(NumberResult, inferredType)) {
    return ParseExpressionResultTypesEnum.NUMBER;
  }

  if (areInferredTypesEqual(DateTimeResult, inferredType)) {
    return ParseExpressionResultTypesEnum.DATE_TIME;
  }

  if (areInferredTypesEqual(BooleanResult, inferredType)) {
    return ParseExpressionResultTypesEnum.BOOLEAN;
  }

  if (isStringLikeInferredType(inferredType)) {
    return ParseExpressionResultTypesEnum.STRING;
  }

  return ParseExpressionResultTypesEnum.OTHER;
}

export function computeAvailableParseExpressionOperations(
  parseExpressionResultType: ParseExpressionResultTypesEnum,
): ReadonlyArray<ParseExpressionOperationsEnum> {
  switch (parseExpressionResultType) {
    case ParseExpressionResultTypesEnum.NUMBER:
      return [
        ParseExpressionOperationsEnum.EQUALS,
        ParseExpressionOperationsEnum.NOT_EQUAL,
        ParseExpressionOperationsEnum.GREATER_THAN,
        ParseExpressionOperationsEnum.LOWER_THAN,
        ParseExpressionOperationsEnum.EXISTS,
        ParseExpressionOperationsEnum.NOT_EXIST,
      ];

    case ParseExpressionResultTypesEnum.BOOLEAN:
      return [ParseExpressionOperationsEnum.IS_TRUE, ParseExpressionOperationsEnum.IS_FALSE];

    case ParseExpressionResultTypesEnum.STRING:
      return [
        ParseExpressionOperationsEnum.CONTAINS,
        ParseExpressionOperationsEnum.NOT_CONTAIN,
        ParseExpressionOperationsEnum.EQUALS,
        ParseExpressionOperationsEnum.NOT_EQUAL,
        ParseExpressionOperationsEnum.STARTS_WITH,
        ParseExpressionOperationsEnum.ENDS_WITH,
        ParseExpressionOperationsEnum.EXISTS,
        ParseExpressionOperationsEnum.NOT_EXIST,
      ];

    case ParseExpressionResultTypesEnum.DATE_TIME:
      return [
        ParseExpressionOperationsEnum.BEFORE,
        ParseExpressionOperationsEnum.AFTER,
        ParseExpressionOperationsEnum.EXISTS,
        ParseExpressionOperationsEnum.NOT_EXIST,
      ];

    case ParseExpressionResultTypesEnum.OTHER:
    default:
      return [ParseExpressionOperationsEnum.EXISTS, ParseExpressionOperationsEnum.NOT_EXIST];
  }
}

export function isParseExpressionUnaryOperation(operation: ParseExpressionOperationsEnum) {
  return unaryOperations.includes(operation);
}

export function isParseExpressionOptionEmpty(option: DecisionTreeNodeDataParseExpressionOptionFrontChat) {
  const {operation, value} = option;

  const isValueEmpty =
    isUndefined(value) || (isString(value) && !value) || (isStringTemplate(value) && isEmpty(value.template));

  return isValueEmpty && !isParseExpressionUnaryOperation(operation);
}

/**
 * Expression types are defined locally but also imported from @frontapp/workflows. These types are very close, but have some differences.
 * @frontapp/workflows defines "server" types, which do not care about the inferrence data. This is almost exactly what we call a "pre-expression" (as in: before inferrence).
 * Because the decision tree types are defined in libs/shared, they cannot import the local types and we end up having to pass "package" types to methods that expects local types.
 * There's probably a better solution, but this is good enough for now.
 */
export function castAsPreExpressions(rExpressions: ReadonlyArray<RExpression>): ReadonlyArray<PreExpression> {
  return rExpressions as ReadonlyArray<PreExpression>;
}

/*
 * Renderers.
 */

/** Translated labels for every kind of parse expression operation. */
export const operationLabels: Record<ParseExpressionOperationsEnum, ReactNode> = {
  [ParseExpressionOperationsEnum.EQUALS]: <Trans id="parse-expression-operation-equals">Equals</Trans>,
  [ParseExpressionOperationsEnum.NOT_EQUAL]: (
    <Trans id="parse-expression-operation-not-equal">Does not equal</Trans>
  ),
  [ParseExpressionOperationsEnum.STARTS_WITH]: (
    <Trans id="parse-expression-operation-starts-with">Starts with</Trans>
  ),
  [ParseExpressionOperationsEnum.ENDS_WITH]: (
    <Trans id="parse-expression-operation-ends-with">Ends with</Trans>
  ),
  [ParseExpressionOperationsEnum.CONTAINS]: <Trans id="parse-expression-operation-contains">Contains</Trans>,
  [ParseExpressionOperationsEnum.NOT_CONTAIN]: (
    <Trans id="parse-expression-operation-not-contain">Does not contain</Trans>
  ),
  [ParseExpressionOperationsEnum.GREATER_THAN]: (
    <Trans id="parse-expression-operation-greater-than">Is greater than</Trans>
  ),
  [ParseExpressionOperationsEnum.LOWER_THAN]: (
    <Trans id="parse-expression-operation-lower-than">Is lower than</Trans>
  ),
  [ParseExpressionOperationsEnum.BEFORE]: <Trans id="parse-expression-operation-before">Is before</Trans>,
  [ParseExpressionOperationsEnum.AFTER]: <Trans id="parse-expression-operation-after">Is after</Trans>,
  [ParseExpressionOperationsEnum.IS_TRUE]: <Trans id="parse-expression-operation-is-true">Is true</Trans>,
  [ParseExpressionOperationsEnum.IS_FALSE]: <Trans id="parse-expression-operation-is-false">Is false</Trans>,
  [ParseExpressionOperationsEnum.EXISTS]: <Trans id="parse-expression-operation-exists">Exists</Trans>,
  [ParseExpressionOperationsEnum.NOT_EXIST]: (
    <Trans id="parse-expression-operation-not-exist">Does not exist</Trans>
  ),
  [ParseExpressionOperationsEnum.ELSE]: <Trans id="parse-expression-operation-else">Else</Trans>,
};
